/**
 * Auth state hook. Wraps a TanStack Query for /api/auth/me so the
 * whole app shares one source of truth on who's logged in. Failures
 * surface as `user === null`; we never throw from this hook (a 401
 * here would otherwise trigger the global redirect from apiFetch).
 */
import { useQuery } from '@tanstack/react-query';

import { api, type UserPublic } from '@/lib/api';

export function useAuthMe(): {
  user: UserPublic | null;
  isLoading: boolean;
  isAdmin: boolean;
  refetch: () => Promise<unknown>;
} {
  const q = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.authMe();
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
  return {
    user: q.data ?? null,
    isLoading: q.isLoading,
    isAdmin: q.data?.role === 'admin',
    refetch: q.refetch,
  };
}
